import {
  ProductGroupFormValue,
  DomainProductGroupFormValue,
  SpaceProductGroupFormValue,
  ProductGroupType,
  ProductGroupListDTO,
  ProductGroupReadDTO,
  ProductGroupCreateDTO,
  AUTO_UPDATE_VALUE,
  ProductGroupUpdateDTO
} from './ProductGroup';
import {
  ProductGroupWebService,
  RestfulProductGroupWebService
} from 'ws/ProductGroupWebService';
import { defaultItemSetting } from 'components/CampaignLimitation/SelectItemComponent';
import i18n from 'i18n';
import {
  AdRequestSourceWebService,
  RestfulAdRequestSourceWebService
} from 'ws/AdRequestSourceWebService';
import { compact, flatMap, isEmpty, uniq } from 'lodash';
import { AddonFeatureType } from 'core/agency/AddonFeature';
import { AddonFeatureManager } from 'core/auth/AddonFeatureManager';
import { AdType } from 'core/rtbCampaign/RtbCampaign';
import { SelectComponent } from 'components/CampaignLimitation/SelectComponent';
import {
  DEFAULT_INVENTORY,
  EmptyLimitationInventorySettings,
  LimitationInventorySettings
} from 'containers/Limitations/LimitationSetting/limitationConfig/limitationSettingsType';
import {
  ChannelGroupSetting,
  getOutdoorDeliveryUnit
} from 'core/channelGroup/ChannelGroupSettings';
import {
  CampaignAdTypeState,
  DeliveryUnit
} from 'core/channelGroup/ChannelGroup';

export interface ProductGroupManager {
  createProductGroup(productGroup: ProductGroupCreateDTO): Promise<void>;
  updateProductGroup(productGroup: ProductGroupUpdateDTO): Promise<void>;
  getProductGroup(): Promise<ProductGroupListDTO[]>;
  getProductGroupById(
    groupId: number,
    valuePrefix: boolean
  ): Promise<ProductGroupReadDTO>;
  getCampaignByGroupId(groupId: number);
  deleteProductGroupById(groupId: number): Promise<void>;
  getInitPropductGroup(
    groupType: ProductGroupType
  ): DomainProductGroupFormValue | SpaceProductGroupFormValue;
  getSpaceAttributesInventorySettings(
    adFormat: string,
    channelGroupId: number,
    spaceAttributes: {
      [key: string]: { [attribute: string]: SelectOptions[] };
    },
    ignoreSpaceAttributes?: string[]
  ): LimitationInventorySettings[];
  getProductGroupValidAdFormatAndChannel(
    isSystemAdmin: boolean,
    addonFeatureManager: AddonFeatureManager
  ): Promise<SelectOptions[]>;
  getRequiredChannelAddonFeaturesOfChannel(channelGroupId: number): string;
  getRequiredCreativesAddonFeaturesOfAdFormat(
    adFormat: string
  ): AddonFeatureType[] | undefined;
}

export class DefaultProductGroupManager implements ProductGroupManager {
  constructor(
    private webService: ProductGroupWebService = new RestfulProductGroupWebService(),
    private adRequestSourceWebService: AdRequestSourceWebService = new RestfulAdRequestSourceWebService()
  ) {}

  createProductGroup(productGroup: ProductGroupCreateDTO): Promise<void> {
    return this.webService.createProductGroup(productGroup);
  }
  updateProductGroup(productGroup: ProductGroupUpdateDTO): Promise<void> {
    return this.webService.updateProductGroup(productGroup);
  }
  getProductGroup(): Promise<ProductGroupListDTO[]> {
    return this.webService.getProductGroup();
  }
  getProductGroupById(
    groupId: number,
    valuePrefix: boolean
  ): Promise<ProductGroupReadDTO> {
    return this.webService.getProductGroupById(groupId, valuePrefix);
  }
  getCampaignByGroupId(groupId: number) {
    return this.webService.getCampaignByGroupId(groupId);
  }
  deleteProductGroupById(groupId: number): Promise<void> {
    return this.webService.deleteProductGroupById(groupId);
  }
  getInitPropductGroup(
    groupType: ProductGroupType
  ): DomainProductGroupFormValue | SpaceProductGroupFormValue {
    const basicPart = {
      groupName: '',
      description: '',
      groupValues: [],
      customGroupValues: [],
      agencyIds: []
    };
    if (groupType === ProductGroupType.DOMAIN) {
      return {
        ...basicPart,
        groupType: ProductGroupType.DOMAIN
      };
    }
    return {
      ...basicPart,
      groupType: ProductGroupType.ADSPACE,
      adFormat: '',
      channel: '',
      channelGroupId: undefined,
      attributes: {}
    };
  }
  async getProductGroupValidAdFormatAndChannel(
    isSystemAdmin: boolean,
    addonFeatureManager: AddonFeatureManager
  ): Promise<SelectOptions[]> {
    const sspSpaceAdFormats =
      await this.adRequestSourceWebService.getSpaceAdFormats();
    return compact(
      sspSpaceAdFormats.map((adFormatOption) => {
        const adFormat = adFormatOption.value.toString();
        let channels = this.getProductGroupChannels(adFormat);
        if (isSystemAdmin) {
          return {
            ...adFormatOption,
            options: channels
          };
        }
        const validChannels = channels.filter((channelOption) => {
          let channelAddon = this.getRequiredChannelAddonFeaturesOfChannel(
            +channelOption.value
          );
          if (
            channelAddon &&
            !addonFeatureManager.isFeatureEnable(
              channelAddon as AddonFeatureType
            )
          ) {
            return false;
          }
          let creativeAddons =
            this.getRequiredCreativesAddonFeaturesOfAdFormat(adFormat);
          if (
            creativeAddons &&
            !addonFeatureManager.isAnyFeatureEnable(...creativeAddons)
          ) {
            return false;
          }
          return true;
        });
        if (validChannels.length > 0) {
          return {
            ...adFormatOption,
            options: validChannels
          };
        }
        return undefined;
      })
    );
  }
  getOutdoorChannelFromId(channelId: number): string | undefined {
    return ChannelGroupSetting.fromId(channelId).config.name;
  }
  getRequiredChannelAddonFeaturesOfChannel(channelGroupId: number): string {
    const channelGroupSetting = ChannelGroupSetting.fromId(channelGroupId);
    return channelGroupSetting.config.addonName;
  }
  getRequiredCreativesAddonFeaturesOfAdFormat(
    adFormat: string
  ): AddonFeatureType[] | undefined {
    if (adFormat === 'Outdoor') {
      return ChannelGroupSetting.values()
        .filter((channelGroupSetting) => channelGroupSetting.isOutdoorChannel())
        .flatMap(
          (channelGroupSetting) =>
            channelGroupSetting.config.campaignAdTypeSettings
        )
        .filter(
          (adTypeSetting) => adTypeSetting.state === CampaignAdTypeState.ACTIVE
        )
        .flatMap((adTypeSetting) => adTypeSetting.bannerTypes)
        .map(
          (bannerTypeSetting) => `option_${bannerTypeSetting.bannerTypeId}`
        ) as AddonFeatureType[];
    }

    const adFormatRTBAdTypeMap = {
      Native: [AdType.DISPLAY],
      Banner: [AdType.DISPLAY],
      Video: [AdType.VIDEO],
      Combo: [AdType.COMBO],
      Straight_Combo: [AdType.COMBO],
      AdNeon: [
        AdType.THIRD_PARTY,
        AdType.THIRD_PARTY_BOTTOM,
        AdType.THIRD_PARTY_RECTANGLE
      ],
      AdNeon_Full_Screen_Rectangle: [
        AdType.THIRD_PARTY,
        AdType.THIRD_PARTY_RECTANGLE
      ],
      AdNeon_Straight_Combo: [AdType.THIRD_PARTY, AdType.THIRD_PARTY_RECTANGLE],
      'Long Rectangle': [AdType.THIRD_PARTY, AdType.THIRD_PARTY_RECTANGLE],
      Bottom: [AdType.THIRD_PARTY, AdType.THIRD_PARTY_BOTTOM],
      Rectangle: [AdType.THIRD_PARTY, AdType.THIRD_PARTY_RECTANGLE]
    };
    const relatedAdTypes = adFormatRTBAdTypeMap[adFormat];
    if (!relatedAdTypes) {
      return undefined;
    }

    return uniq(
      flatMap(
        relatedAdTypes.map((adType) => {
          const adTypeSetting =
            ChannelGroupSetting.adTypeSettings().get(adType);
          if (!adTypeSetting) {
            return [];
          }
          return adTypeSetting.bannerTypes.map(
            (bannerTypeSetting) => `option_${bannerTypeSetting.bannerTypeId}`
          );
        })
      )
    );
  }
  getProductGroupChannels(adFormat: string): SelectOptions[] {
    if (adFormat === '') {
      return [];
    }

    let channels: { [key: string]: number };
    if (adFormat === 'Outdoor') {
      // ECOPD-5566: 這是產品群組表單上「使用者可選」的渠道下拉，必須排除停用渠道。
      // 改動前停用渠道根本沒被註冊所以不會出現；改成照樣註冊之後就得顯式擋，
      // 否則會造出「選得到但投遞不出去」——正是本單要消滅的東西。
      // （system admin 走 getProductGroupValidAdFormatAndChannel 的 early return，
      //  完全不過 addon 過濾，所以不能指望下游擋。）
      channels = ChannelGroupSetting.activeValues()
        .filter((setting) => setting.isOutdoorChannel())
        .reduce((acc, setting) => {
          acc[setting.key] = setting.config.id;
          return acc;
        }, {});
    } else {
      channels = {
        [ChannelGroupSetting.RTB.key]: ChannelGroupSetting.RTB.config.id
      };
    }
    return Object.entries(channels).map(([label, value]) => ({ label, value }));
  }

  getSpaceAttributesInventorySettings(
    adFormat: string,
    channelGroupId: number | undefined,
    spaceAttributes: {
      [key: string]: { [attribute: string]: SelectOptions[] };
    },
    ignoreSpaceAttributes: string[] = []
  ): LimitationInventorySettings[] {
    const defaultInventorySetting: EmptyLimitationInventorySettings = {
      name: DEFAULT_INVENTORY
    };
    if (channelGroupId === undefined) {
      return [defaultInventorySetting];
    }

    const relatedAttributes = spaceAttributes[`${adFormat}-${channelGroupId}`];
    if (!relatedAttributes) {
      return [defaultInventorySetting];
    }
    const options = Object.keys(relatedAttributes)
      .filter((attribute) => !ignoreSpaceAttributes.includes(attribute))
      .map((attribute) => ({
        name: attribute,
        title: i18n.t(`productGroup.space.attributes.titles.${attribute}`),
        singleSelect: false,
        itemSetting: defaultItemSetting(),
        searchPlaceholder: 'productGroup.placeholders.search',
        component: SelectComponent,
        cb: async () => relatedAttributes[attribute]
      }));
    return [defaultInventorySetting, ...options];
  }
}

export function toServerPayload(
  productGroup: ProductGroupFormValue,
  allOptions: SelectOptions[]
): ProductGroupCreateDTO {
  const translateGroupValues = (groupValues: SelectOptions[]) => {
    return groupValues.map((option) => {
      const { label, value } = option;
      const newName = label.replace(`${value},`, '');
      return {
        limitationName: newName,
        limitationValue: value.toString()
      };
    });
  };

  const groupValues = productGroup.groupValues;
  const autoUpdateOption = groupValues.find(
    (option) => option.value === AUTO_UPDATE_VALUE
  );
  const isSpace = productGroup.groupType === ProductGroupType.ADSPACE;
  const deliveryUnit = isSpace
    ? getOutdoorDeliveryUnit(productGroup.adFormat, productGroup.channelGroupId)
    : undefined;
  const isStoreUnit = deliveryUnit === DeliveryUnit.STORE;
  const isDeviceUnit = deliveryUnit === DeliveryUnit.DEVICE;
  const autoUpdate =
    autoUpdateOption !== undefined || isStoreUnit || isDeviceUnit;
  const ignoreEmptyString = (value) => (isEmpty(value) ? undefined : value);
  return {
    ...productGroup,
    groupValues:
      isStoreUnit || isDeviceUnit
        ? []
        : autoUpdate
          ? translateGroupValues(allOptions)
          : translateGroupValues(groupValues),
    customGroupValues: translateGroupValues(productGroup.customGroupValues),
    adFormat: isSpace ? ignoreEmptyString(productGroup.adFormat) : undefined,
    channel: isSpace ? ignoreEmptyString(productGroup.channel) : undefined,
    attributes: isSpace ? productGroup.attributes : undefined,
    autoUpdate
  };
}
